import { useState, useCallback, useRef, useEffect } from "react";
import { quotationService } from "../../../services/quotation.service";

export function useQuotationDraft(quotationId = null) {
  // State
  const [quotation, setQuotation] = useState({
    id: null,
    status: "draft",
    type: "goods",
    quoteNumber: "",
    quoteDate: new Date().toISOString().slice(0, 10),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // 30 days from now
    poDate: null,
    agreementNumber: null,
    poNumber: null,
    paymentTerms: "due-on-receipt",
    client: null,
    billingAddress: "",
    shippingAddress: "",
    items: [],
    notes: "",
    privateNotes: "",
    clientNotes: "",
    discount: 0,
    discountType: "flat",
    shipping: 0,
    roundOff: 0,
    total: 0,
    convertedInvoiceId: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false); // Track if user has entered any data

  // Refs
  const saveTimeoutRef = useRef(null);
  const hasChangesRef = useRef(false);

  /* ═══════════════════════════════════════════════════════════
     CREATE QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const createQuotation = useCallback(async () => {
    if (quotation.id) return; // Already created

    setIsLoading(true);
    setError(null);

    try {
      const result = await quotationService.create({
        type: quotation.type,
        quote_date: quotation.quoteDate,
        valid_until: quotation.validUntil,
        payment_terms: quotation.paymentTerms,
        status: "draft",
      });

      setQuotation((prev) => ({
        ...prev,
        id: result.id,
        quoteNumber: result.quote_number,
      }));

      setLastSaved(new Date());
    } catch (err) {
      console.error("Failed to create quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [quotation.id, quotation.type, quotation.quoteDate, quotation.validUntil, quotation.paymentTerms]);

  /* ═══════════════════════════════════════════════════════════
     LOAD QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const loadQuotation = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await quotationService.get(id);

      // Build client object from data
      let client = null;
      if (data.client_id) {
        // If we have client fields in the quotation, use them
        if (data.client_name) {
          client = {
            id: data.client_id,
            name: data.client_name,
            email: data.client_email,
            phone: data.client_phone,
            gstin: data.client_gstin,
            billing_address: data.client_billing_address,
            city: data.client_city,
            state: data.client_state,
            pincode: data.client_pincode,
          };
        } else {
          // Otherwise, fetch the client from the database
          try {
            const { clientService } = await import('../../../services/client.service');
            const clientData = await clientService.get(data.client_id);
            if (clientData) {
              client = {
                id: clientData.id || clientData._id,
                name: clientData.name,
                email: clientData.email,
                phone: clientData.phone,
                gstin: clientData.gstin,
                billing_address: clientData.billing_address,
                city: clientData.city,
                state: clientData.state,
                pincode: clientData.pincode,
              };
            }
          } catch (err) {
            console.warn('[useQuotationDraft] Failed to fetch client:', err);
          }
        }
      }

      setQuotation({
        id: data.id,
        status: data.status,
        type: data.quotationType || data.type || 'goods',  // Handle both field names
        quoteNumber: data.quote_number,
        quoteDate: data.quote_date || data.issue_date,  // Handle both field names
        validUntil: data.valid_until,
        poDate: data.po_date,
        agreementNumber: data.agreement_number,
        poNumber: data.po_number || null,
        paymentTerms: data.payment_terms,
        client: client,
        items: (data.items || []).map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          type: item.type,
          quantity: item.quantity || 1,
          rate: item.rate || item.price || 0,
          discount: item.discount || 0,
          discountType: item.discount_type || item.discountType || 'flat',
          gstRate: item.gst_rate || item.gstRate || item.tax || 0,
          gstType: item.gst_type || item.gstType || 'none',
          hsn: item.hsn || '',
          sac: item.sac || '',
          uom: item.uom || 'unit',
          sourceItemId: item.source_item_id || item.sourceItemId,
          isOverridden: item.is_overridden || item.isOverridden || false,
          cgst_amount: item.cgst_amount,
          sgst_amount: item.sgst_amount,
          igst_amount: item.igst_amount,
          tax: item.tax,
          amount: item.amount,
        })),
        notes: data.notes || "",
        discount: data.discount || 0,
        discountType: data.discount_type || data.discountType || "flat",
        shipping: data.shipping || 0,
        roundOff: data.round_off || data.roundOff || 0,
        total: data.total || 0,
        convertedInvoiceId: data.converted_invoice_id || data.converted_to_invoice_id,
      });

      setLastSaved(new Date(data.updated_at));
      setHasUserInput(true); // Existing quotation means user has already entered data
    } catch (err) {
      console.error("Failed to load quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* ═══════════════════════════════════════════════════════════
     SAVE DRAFT (with debouncing)
     ═══════════════════════════════════════════════════════════ */
  const saveDraft = useCallback(async () => {
    if (!quotation.id) return;

    setIsSaving(true);
    setError(null);

    try {
      const updatedQuotation = await quotationService.save(quotation.id, {
        quoteNumber: quotation.quoteNumber,
        type: quotation.type,
        client: quotation.client,
        quoteDate: quotation.quoteDate,
        validUntil: quotation.validUntil,
        poDate: quotation.poDate,
        agreementNumber: quotation.agreementNumber,
        poNumber: quotation.poNumber,
        paymentTerms: quotation.paymentTerms,
        discount: quotation.discount,
        discountType: quotation.discountType,
        shipping: quotation.shipping,
        roundOff: quotation.roundOff,
        items: quotation.items,
        notes: quotation.notes,
        status: quotation.status,
      });

      // Update local state with server response
      if (updatedQuotation) {
        setQuotation((prev) => ({
          ...prev,
          total: updatedQuotation.total || 0,
          subtotal: updatedQuotation.subtotal || 0,
          taxTotal: updatedQuotation.tax_total || 0,
        }));
      }

      setLastSaved(new Date());
      hasChangesRef.current = false;
    } catch (err) {
      console.error("Failed to save quotation:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [quotation]);

  /* ═══════════════════════════════════════════════════════════
     SEND QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const sendQuotation = useCallback(async () => {
    if (!quotation.id) {
      throw new Error("Quotation must be created before sending");
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await quotationService.send(quotation.id, {
        items: quotation.items,
      });

      setQuotation((prev) => ({
        ...prev,
        status: result.status,
      }));

      setLastSaved(new Date());
      return result;
    } catch (err) {
      console.error("Failed to send quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [quotation.id, quotation.items]);

  /* ═══════════════════════════════════════════════════════════
     RESEND QUOTATION
     🔥 Resends email for already sent quotation
     ═══════════════════════════════════════════════════════════ */
  const resendQuotation = useCallback(async () => {
    if (!quotation.id) {
      throw new Error("Quotation must be created before resending");
    }

    if (quotation.status !== "sent" && quotation.status !== "accepted") {
      throw new Error("Only sent or accepted quotations can be resent");
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await quotationService.resend(quotation.id);
      setLastSaved(new Date());
      return result;
    } catch (err) {
      console.error("Failed to resend quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [quotation.id, quotation.status]);

  /* ═══════════════════════════════════════════════════════════
     DELETE QUOTATION
     ═══════════════════════════════════════════════════════════ */
  const deleteQuotation = useCallback(async () => {
    if (!quotation.id) {
      throw new Error("Quotation must be created before deleting");
    }

    if (quotation.status !== "draft") {
      throw new Error("Only draft quotations can be deleted");
    }

    setIsLoading(true);
    setError(null);

    try {
      await quotationService.delete(quotation.id);
    } catch (err) {
      console.error("Failed to delete quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [quotation.id, quotation.status]);

  /* ═══════════════════════════════════════════════════════════
     CONVERT TO INVOICE
     ═══════════════════════════════════════════════════════════ */
  const convertToInvoice = useCallback(async () => {
    if (!quotation.id) {
      throw new Error("Quotation must be created before converting");
    }

    if (quotation.convertedInvoiceId) {
      throw new Error("Quotation has already been converted");
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await quotationService.convert(quotation.id);

      setQuotation((prev) => ({
        ...prev,
        status: "converted",
        convertedInvoiceId: result.invoice.id,
      }));

      setLastSaved(new Date());
      return result;
    } catch (err) {
      console.error("Failed to convert quotation:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [quotation.id, quotation.convertedInvoiceId]);

  /* ═══════════════════════════════════════════════════════════
     HELPER: CHECK IF QUOTATION HAS MEANINGFUL DATA
     ═══════════════════════════════════════════════════════════ */
  const hasMeaningfulData = useCallback((quotationData) => {
    // Check if any meaningful fields have been filled (excluding default values)
    return (
      (quotationData.client && quotationData.client.id) ||
      (quotationData.items && quotationData.items.length > 0) ||
      (quotationData.notes && quotationData.notes.trim().length > 0) ||
      (quotationData.discount && quotationData.discount > 0) ||
      (quotationData.shipping && quotationData.shipping > 0) ||
      (quotationData.roundOff && quotationData.roundOff !== 0) ||
      (quotationData.agreementNumber && quotationData.agreementNumber.trim().length > 0) ||
      // Only consider paymentTerms meaningful if it's different from default
      (quotationData.paymentTerms && quotationData.paymentTerms !== "due-on-receipt")
    );
  }, []);

  /* ═══════════════════════════════════════════════════════════
     UPDATE FIELD
     ═══════════════════════════════════════════════════════════ */
  const updateField = useCallback((field, value) => {
    setQuotation((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // Clear items when quotation type changes (goods <-> services)
      if (field === 'type' && prev.type && prev.type !== value && prev.items && prev.items.length > 0) {
        updated.items = [];
      }
      
      // Check if this is the first meaningful input
      if (!hasUserInput && hasMeaningfulData(updated)) {
        setHasUserInput(true);
      }
      
      return updated;
    });
    hasChangesRef.current = true;
  }, [hasUserInput, hasMeaningfulData]);

  /* ═══════════════════════════════════════════════════════════
     AUTO-CREATE QUOTATION ON MOUNT OR FIRST INPUT
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (quotationId) {
      loadQuotation(quotationId);
    }
    // Don't auto-create quotation on mount - wait for user input
  }, [quotationId]);

  // Create quotation when user first enters meaningful data
  useEffect(() => {
    if (!quotationId && !quotation.id && hasUserInput) {
      createQuotation();
    }
  }, [quotationId, quotation.id, hasUserInput, createQuotation]);

  /* ═══════════════════════════════════════════════════════════
     AUTOSAVE WITH DEBOUNCE
     🔥 CRITICAL: Only autosave draft quotations to prevent data corruption
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    // CRITICAL: Do not autosave sent/converted quotations
    // This prevents overwriting existing data with empty items
    if (!quotation.id || !hasChangesRef.current || quotation.status !== 'draft') {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveDraft();
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [quotation, saveDraft]);

  /* ═══════════════════════════════════════════════════════════
     SAVE ON UNMOUNT
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    return () => {
      if (quotation.id && hasChangesRef.current) {
        quotationService.save(quotation.id, {
          quoteNumber: quotation.quoteNumber,
          type: quotation.type,
          client: quotation.client,
          quoteDate: quotation.quoteDate,
          validUntil: quotation.validUntil,
          poDate: quotation.poDate,
          agreementNumber: quotation.agreementNumber,
        poNumber: quotation.poNumber,
          paymentTerms: quotation.paymentTerms,
          discount: quotation.discount,
          discountType: quotation.discountType,
          shipping: quotation.shipping,
          roundOff: quotation.roundOff,
          items: quotation.items,
          notes: quotation.notes,
          status: quotation.status,
        }).catch((err) => {
          console.error("Failed to save on unmount:", err);
        });
      }
    };
  }, [quotation]);

  return {
    quotation,
    updateField,
    createQuotation,
    loadQuotation,
    saveDraft,
    sendQuotation,
    resendQuotation,
    deleteQuotation,
    convertToInvoice,
    isLoading,
    isSaving,
    lastSaved,
    error,
    hasUnsavedChanges: hasChangesRef.current,
  };
}
